import React from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { format, addYears, addMonths } from "date-fns";
import {
  getCollateralDescription,
  getCasteLabel,
  guarantorFieldConfig,
  collateralTypeConfig,
} from "../formConfig";
import { formatINR, formatDisplayDate } from "../../../helpers";

const displayText = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const displayINR = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = parseFloat(value);
  if (Number.isNaN(num)) return null;
  return formatINR(num);
};

const displayDate = (value) => formatDisplayDate(value);

const findBankAccount = (bankAccounts, id) => {
  if (!id) return null;
  return bankAccounts.find((a) => String(a.id) === String(id));
};

const ReviewField = ({ label, value, col = 6 }) => (
  <Col md={col} className="mb-3">
    <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.02em" }}>
      {label}
    </div>
    <div className="fw-medium text-break">{value ?? "-"}</div>
  </Col>
);

const ReviewHtmlField = ({ label, html, col = 12 }) => (
  <Col md={col} className="mb-3">
    <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.02em" }}>
      {label}
    </div>
    {html ? (
      <div className="border rounded p-2 bg-white" dangerouslySetInnerHTML={{ __html: html }} />
    ) : (
      <div className="fw-medium text-muted">-</div>
    )}
  </Col>
);

const ReviewFileField = ({ label, url, col = 6 }) => (
  <Col md={col} className="mb-3">
    <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.02em" }}>
      {label}
    </div>
    {url ? (
      <img
        src={url}
        alt={label}
        className="rounded border"
        style={{ maxHeight: 100, maxWidth: 160, objectFit: "cover" }}
      />
    ) : (
      <div className="fw-medium text-muted">-</div>
    )}
  </Col>
);

const ReviewSection = ({ title, children }) => (
  <Card className="mb-4 shadow-sm">
    <Card.Header className="bg-light">
      <h6 className="mb-0 fw-semibold">{title}</h6>
    </Card.Header>
    <Card.Body>{children}</Card.Body>
  </Card>
);

const ReviewStep = ({ loanTypes, collateralTypes, guarantors, collaterals, bankAccounts }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext();

  const getLoanEndDateDisplay = () => {
    if (values.loanEndDate) return displayDate(values.loanEndDate);
    if (values.loanStartDate && values.durationYears !== "" && values.durationMonths !== "") {
      let endDate = new Date(values.loanStartDate);
      endDate = addYears(endDate, parseInt(values.durationYears, 10) || 0);
      endDate = addMonths(endDate, parseInt(values.durationMonths, 10) || 0);
      return formatDisplayDate(endDate);
    }
    return null;
  };

  const getCollateralTypeLabel = (type) =>
    collateralTypes.find((ct) => ct.value === type)?.label ||
    t(`customer.add.collateralTypes.${type}`, { defaultValue: type });

  const formatGuarantorField = (field, guarantor) => {
    const value = guarantor[field.name];
    if (field.type === "file") return value;
    if (field.name === "idProofType" && value) {
      return t(`customer.add.step2.idProofTypes.${value}`, { defaultValue: value });
    }
    if (field.name === "monthlyIncome") return displayINR(value);
    return displayText(value);
  };

  const formatCollateralField = (field, collateral) => {
    const value = collateral[field.name];
    if (field.type === "file") return value;
    if (field.type === "date") return displayDate(value);
    if (field.name === "condition" && value) {
      return t(`customer.add.step2.conditionOptions.${value}`, { defaultValue: value });
    }
    if (field.name === "estimatedValue" || field.name === "value" || field.name === "monthlyIncome") {
      return displayINR(value);
    }
    if (field.name === "weightMilligrams" && value !== "" && value != null) {
      return `${value} mg`;
    }
    if (field.name === "weightGrams" && value !== "" && value != null) {
      return `${value} g`;
    }
    return displayText(value);
  };

  const customerTypeLabel =
    values.customerType === "existing"
      ? t("customer.add.step1.existingCustomer")
      : values.customerType === "new"
        ? t("customer.add.step1.newCustomer")
        : null;

  const interestTypeLabel =
    values.interestType === "fixed"
      ? t("customer.add.step3.fixedInterest")
      : values.interestType === "reducing"
        ? t("customer.add.step3.reductibleInterest")
        : null;

  const paymentModeLabel = (mode) =>
    mode === "cash"
      ? t("customer.add.step3.cash")
      : mode === "bank"
        ? t("customer.add.step3.bank")
        : null;

  const formatBankAccount = (id) => {
    const account = findBankAccount(bankAccounts, id);
    return account ? `${account.accountNumber} - ${account.bankName}` : null;
  };

  return (
    <div>
      <h5 className="mb-4">{t("customer.add.steps.step4")}</h5>

      {/* Step 1 — Customer Information */}
      <ReviewSection title={t("customer.add.steps.step1")}>
        <Row>
          <ReviewField label={t("customer.add.step1.customerType")} value={customerTypeLabel} />
          <ReviewField label={t("customer.add.step1.date")} value={displayDate(values.date)} />
          <ReviewField label={t("customer.add.step1.billNo")} value={displayText(values.billNo)} />
          <ReviewField
            label={t("customer.add.step1.loanType")}
            value={loanTypes.find((lt) => lt.value === values.loanType)?.label}
          />
          <ReviewField label={t("customer.add.step1.name")} value={displayText(values.name)} />
          <ReviewField label={t("customer.add.step1.mobileNumber")} value={displayText(values.mobileNumber)} />
          <ReviewField label={t("customer.add.step1.email")} value={displayText(values.email)} />
          <ReviewField label={t("customer.add.step1.aadharCard")} value={displayText(values.aadharCard)} />
          <ReviewField label={t("customer.add.step1.panCard")} value={displayText(values.panCard)} />
          <ReviewField label={t("customer.add.step1.caste")} value={getCasteLabel(values.caste, t) || null} />
          <ReviewField label={t("customer.add.step1.occupation")} value={displayText(values.occupation)} />
          <ReviewField label={t("customer.add.step1.company")} value={displayText(values.company)} />
          <ReviewField label={t("customer.add.step1.address")} value={displayText(values.address)} col={12} />
          <ReviewFileField label={t("customer.add.step1.uploadPhoto")} url={values.photo} col={12} />
        </Row>
      </ReviewSection>

      {/* Step 2 — Guarantors & Collaterals */}
      <ReviewSection title={t("customer.add.steps.step2")}>
        <h6 className="fw-semibold mb-3">{t("customer.add.step2.guarantorSectionTitle")}</h6>
        {guarantors.length === 0 ? (
          <p className="text-muted mb-4">{t("customer.add.step2.noGuarantors")}</p>
        ) : (
          guarantors.map((guarantor, idx) => (
            <Card key={guarantor.id} className="mb-3 border-start border-primary border-3">
              <Card.Header className="py-2 bg-white">
                <span className="fw-semibold">
                  {t("customer.add.review.guarantorNumber", { number: idx + 1 })}
                  {guarantor.fullName ? ` — ${guarantor.fullName}` : ""}
                </span>
              </Card.Header>
              <Card.Body className="pt-3">
                <Row>
                  {guarantorFieldConfig.map((field) => {
                    if (field.type === "file") {
                      return (
                        <ReviewFileField
                          key={field.name}
                          label={t(field.labelKey)}
                          url={guarantor[field.name]}
                          col={field.col || 6}
                        />
                      );
                    }
                    if (field.type === "richtext") {
                      return (
                        <ReviewHtmlField
                          key={field.name}
                          label={t(field.labelKey)}
                          html={guarantor[field.name]}
                          col={field.col || 12}
                        />
                      );
                    }
                    if (field.type === "textarea") {
                      return (
                        <ReviewField
                          key={field.name}
                          label={t(field.labelKey)}
                          value={displayText(guarantor[field.name])}
                          col={field.col || 12}
                        />
                      );
                    }
                    return (
                      <ReviewField
                        key={field.name}
                        label={t(field.labelKey)}
                        value={formatGuarantorField(field, guarantor)}
                        col={field.col || 6}
                      />
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          ))
        )}

        <h6 className="fw-semibold mb-3 mt-2">{t("customer.add.step2.collateralSectionTitle")}</h6>
        {collaterals.length === 0 ? (
          <p className="text-muted mb-0">{t("customer.add.step2.noCollaterals")}</p>
        ) : (
          collaterals.map((collateral, idx) => {
            const typeFields = collateralTypeConfig[collateral.type]?.fields || [];
            return (
              <Card key={collateral.id} className="mb-3 border-start border-success border-3">
                <Card.Header className="py-2 bg-white">
                  <span className="fw-semibold">
                    {t("customer.add.review.collateralNumber", { number: idx + 1 })}
                    {` — ${getCollateralTypeLabel(collateral.type)}`}
                  </span>
                  {getCollateralDescription(collateral) && getCollateralDescription(collateral) !== "-" && (
                    <div className="small text-muted mt-1">{getCollateralDescription(collateral)}</div>
                  )}
                </Card.Header>
                <Card.Body className="pt-3">
                  <Row>
                    {typeFields.map((field) => {
                      if (field.type === "file") {
                        return (
                          <ReviewFileField
                            key={field.name}
                            label={t(field.labelKey)}
                            url={collateral[field.name]}
                            col={6}
                          />
                        );
                      }
                      if (field.type === "richtext") {
                        return (
                          <ReviewHtmlField
                            key={field.name}
                            label={t(field.labelKey)}
                            html={collateral[field.name]}
                            col={12}
                          />
                        );
                      }
                      if (field.type === "textarea") {
                        return (
                          <ReviewField
                            key={field.name}
                            label={t(field.labelKey)}
                            value={displayText(collateral[field.name])}
                            col={12}
                          />
                        );
                      }
                      return (
                        <ReviewField
                          key={field.name}
                          label={t(field.labelKey)}
                          value={formatCollateralField(field, collateral)}
                          col={6}
                        />
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>
            );
          })
        )}
      </ReviewSection>

      {/* Step 3 — Loan Details */}
      <ReviewSection title={t("customer.add.steps.step3")}>
        <Row>
          <ReviewField label={t("customer.add.step3.loanAmount")} value={displayINR(values.loanAmount)} />
          <ReviewField
            label={t("customer.add.step3.interestPerYear")}
            value={displayText(values.interestPerYear) ? `${values.interestPerYear}%` : null}
          />
          <ReviewField label={t("customer.add.step3.loanStartDate")} value={displayDate(values.loanStartDate)} />
          <ReviewField
            label={t("customer.add.step3.duration")}
            value={
              values.durationYears !== "" || values.durationMonths !== ""
                ? `${values.durationYears || 0} ${t("customer.add.step3.years")} ${values.durationMonths || 0} ${t("customer.add.step3.months")}`
                : null
            }
          />
          <ReviewField label={t("customer.add.step3.loanEndDate")} value={getLoanEndDateDisplay()} />
          <ReviewField label={t("customer.add.step3.interestType")} value={interestTypeLabel} />
          <ReviewField
            label={t("customer.add.step3.monthlyInstallment")}
            value={
              values.monthlyInstallment
                ? t("customer.add.common.yes")
                : t("customer.add.common.no")
            }
          />

          {values.monthlyInstallment && (
            <>
              <ReviewField label={t("customer.add.step3.firstEMIDate")} value={displayDate(values.firstEMIDate)} />
              <ReviewField label={t("customer.add.step3.totalInstallments")} value={displayText(values.totalInstallments)} />
              <ReviewField
                label={t("customer.add.step3.monthlyInstallmentAmount")}
                value={displayINR(values.monthlyInstallmentAmount)}
              />
            </>
          )}

          {(values.interestType === "fixed" || values.interestType === "reducing") && (
            <>
              <ReviewField label={t("customer.add.step3.totalInterest")} value={displayINR(values.totalInterest)} />
              <ReviewField
                label={t("customer.add.step3.totalAmountPayable")}
                value={displayINR(values.totalAmountPayable)}
              />
            </>
          )}

          <ReviewField label={t("customer.add.step3.processingCharge")} value={displayINR(values?.processingCharge || 0)} />

          {values.processingCharge && values.processingCharge !== "" && parseFloat(values.processingCharge) > 0 && (
            <>
              <ReviewField
                label={t("customer.add.step3.processingChargeReceivedMode")}
                value={paymentModeLabel(values.processingChargeReceivedMode)}
              />
              {values.processingChargeReceivedMode === "bank" && (
                <>
                  <ReviewField
                    label={t("customer.add.step3.processingChargeBankAccount")}
                    value={formatBankAccount(values.processingChargeBankAccount)}
                  />
                  <ReviewField
                    label={t("customer.add.step3.processingChargeTransactionNo")}
                    value={displayText(values?.processingChargeTransactionNo || 0)}
                  />
                </>
              )}
            </>
          )}

          <ReviewField
            label={t("customer.add.step3.reminder")}
            value={values.reminder ? t("customer.add.common.yes") : t("customer.add.common.no")}
          />
          <ReviewField label={t("customer.add.step3.paymentType")} value={paymentModeLabel(values.paymentType)} />

          {values.paymentType === "bank" && (
            <>
              <ReviewField label={t("customer.add.step3.selectBankAccount")} value={formatBankAccount(values.bankAccount)} />
              <ReviewField
                label={t("customer.add.step3.chequeTransactionNo")}
                value={displayText(values.chequeTransactionNo)}
              />
            </>
          )}
        </Row>
      </ReviewSection>
    </div>
  );
};

export default ReviewStep;
